import * as React from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import '@fontsource-variable/inter';
import { createAppTheme } from './theme';
import { useBlurOnWheel } from './useBlurOnWheel';
import { inputAutofillKeyframes } from './inputAutofill';
import { PRESET_STORAGE_KEY, THEME_PRESETS, getPreset, readStoredPresetId, type ThemePreset } from './presets';
import {
  FONT_SIZE_OPTIONS,
  FONT_SIZE_STORAGE_KEY,
  applyFontSize,
  getFontSize,
  readStoredFontSizeId,
  type FontSizeOption,
} from './fontSize';

/** Same key CoreUI's useColorModes used, so existing users keep their preference. */
export const COLOR_MODE_STORAGE_KEY = 'color-theme-zuul';

interface ThemePresetContextValue {
  presetId: string;
  setPresetId: (id: string) => void;
}

const ThemePresetContext = React.createContext<ThemePresetContextValue | null>(null);

interface FontSizeContextValue {
  fontSizeId: string;
  setFontSizeId: (id: string) => void;
}

const FontSizeContext = React.createContext<FontSizeContextValue | null>(null);

interface AppThemeProviderProps {
  children: React.ReactNode;
  /** Overrides the persisted preset (tests). */
  initialPresetId?: string;
  /** Overrides the persisted font size (tests). */
  initialFontSizeId?: string;
}

export function AppThemeProvider({ children, initialPresetId, initialFontSizeId }: AppThemeProviderProps) {
  const [presetId, setPresetIdState] = React.useState(() => getPreset(initialPresetId ?? readStoredPresetId()).id);

  const setPresetId = React.useCallback((id: string) => {
    const next = getPreset(id).id;
    setPresetIdState(next);
    try {
      localStorage.setItem(PRESET_STORAGE_KEY, next);
    } catch {
      /* storage unavailable (private mode); the choice still applies for this session */
    }
  }, []);

  const presetContext = React.useMemo(() => ({ presetId, setPresetId }), [presetId, setPresetId]);

  const [fontSizeId, setFontSizeIdState] = React.useState(
    () => getFontSize(initialFontSizeId ?? readStoredFontSizeId()).id
  );

  const setFontSizeId = React.useCallback((id: string) => {
    const next = getFontSize(id).id;
    setFontSizeIdState(next);
    try {
      localStorage.setItem(FONT_SIZE_STORAGE_KEY, next);
    } catch {
      /* storage unavailable (private mode); the choice still applies for this session */
    }
  }, []);

  // The size lives on <html>, outside React's tree, so it is a side effect rather than a style
  // prop: one declaration scales every rem value in the app without re-rendering anything.
  React.useEffect(() => {
    applyFontSize(getFontSize(fontSizeId));
  }, [fontSizeId]);

  // Also document-level: the wheel must never step a focused number input or select anywhere in the app.
  useBlurOnWheel();

  const fontSizeContext = React.useMemo(() => ({ fontSizeId, setFontSizeId }), [fontSizeId, setFontSizeId]);
  // One theme per preset, built once and cached; MUI regenerates its CSS variable stylesheet for
  // both color schemes, so the light/dark toggle keeps working against whichever preset is active.
  const theme = React.useMemo(() => createAppTheme(getPreset(presetId)), [presetId]);

  return (
    // injectFirst: emotion styles go to the top of <head>, so the co-located CSS a component keeps
    // beside it can still override MUI without !important.
    <StyledEngineProvider injectFirst>
      <ThemeProvider
        theme={theme}
        modeStorageKey={COLOR_MODE_STORAGE_KEY}
        defaultMode='system'
        noSsr
        disableTransitionOnChange
      >
        {/* Bootstrap's reboot supplied the element resets until phase 8; this is what replaces it. */}
        <CssBaseline />
        {/* Once for the whole app; every input has disableInjectingGlobalStyles (see ./inputAutofill). */}
        <GlobalStyles styles={inputAutofillKeyframes} />
        <ThemePresetContext.Provider value={presetContext}>
          <FontSizeContext.Provider value={fontSizeContext}>
            {/* Date pickers read their adapter from context, so every one of them gets date-fns —
                the library this app already formats dates with — without wiring it per call site. */}
            <LocalizationProvider dateAdapter={AdapterDateFns}>{children}</LocalizationProvider>
          </FontSizeContext.Provider>
        </ThemePresetContext.Provider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

/** The active color preset and a setter; the list of presets is exposed for pickers. */
export function useThemePreset(): ThemePresetContextValue & { preset: ThemePreset; presets: readonly ThemePreset[] } {
  const context = React.useContext(ThemePresetContext);
  if (!context) throw new Error('useThemePreset must be used within AppThemeProvider');
  return { ...context, preset: getPreset(context.presetId), presets: THEME_PRESETS };
}

/** The active font size preset and a setter; the list of presets is exposed for pickers. */
export function useFontSize(): FontSizeContextValue & {
  fontSize: FontSizeOption;
  fontSizes: readonly FontSizeOption[];
} {
  const context = React.useContext(FontSizeContext);
  if (!context) throw new Error('useFontSize must be used within AppThemeProvider');
  return { ...context, fontSize: getFontSize(context.fontSizeId), fontSizes: FONT_SIZE_OPTIONS };
}
